import { parseISO } from 'date-fns';
import {
  HeurekaConditionDto,
  HeurekaMedicationStatementDto,
  HeurekaPatientDetailsWithPracticeAndInsuranceDto,
  HeurekaPatientDto,
  MedicationDosageDtoDetails,
  useGetHeurekaPatientSuspense,
  useGetMedi24PatientDetailsSuspense,
  useGetSante24PatientDetailsSuspense,
} from './generated';

export interface HeurekaPatient extends Omit<HeurekaPatientDto, 'dateOfBirth'> {
  dateOfBirth?: Date;
}

export interface HeurekaMedicationStatement extends Omit<
  HeurekaMedicationStatementDto,
  'from' | 'until' | 'dosingSchedule'
> {
  from?: Date;
  until?: Date;
  dosingSchedule: MedicationDosageDtoDetails | undefined;
}

export interface HeurekaCondition extends Omit<HeurekaConditionDto, 'from' | 'until' | 'recorded'> {
  from?: Date;
  until?: Date;
  recorded?: Date;
}

export interface PatientWithHeurekaData {
  practiceName?: string | null;
  practiceEmail?: string | null;
  insuranceName?: string | null;
  insurancePatientNumber?: string | null;
  heurekaDetails: HeurekaPatientDetails | null;
}

export interface HeurekaPatientDetails {
  patient: HeurekaPatient;
  medicationStatements: HeurekaMedicationStatement[];
  conditions: HeurekaCondition[] | null;
}

export const useGetHeurekaPatientTransformedSuspense = (patientId: string) => {
  return useGetHeurekaPatientSuspense(patientId, { query: { select: transformHeurekaPatientDetails } });
};

export const useGetMedi24PatientDetailsTransformedSuspense = (patientId: string) => {
  return useGetMedi24PatientDetailsSuspense(patientId, { query: { select: transformHeurekaPatientDetails } });
};

export const useGetSante24PatientDetailsTransformedSuspense = (patientId: string) => {
  return useGetSante24PatientDetailsSuspense(patientId, { query: { select: transformHeurekaPatientDetails } });
};

const transformHeurekaPatientDetails = (
  details: HeurekaPatientDetailsWithPracticeAndInsuranceDto,
): PatientWithHeurekaData => ({
  practiceName: details.practiceName,
  practiceEmail: details.practiceEmail,
  insuranceName: details.insuranceName,
  insurancePatientNumber: details.insurancePatientNumber,
  heurekaDetails: details.heurekaDetails
    ? {
        patient: transformHeurekaPatient(details.heurekaDetails.patient),
        medicationStatements: details.heurekaDetails.medicationStatements.map((medicationStatement) => {
          const mornNoonEveNight = replaceDecimalsWithFractions(
            medicationStatement.dosage.mornNoonEveNight?.join(' - ') ?? '',
          );
          const dosingSchedule = mornNoonEveNight ? [mornNoonEveNight] : medicationStatement.dosage.details;

          return {
            ...medicationStatement,
            dosingSchedule,
            from: medicationStatement.from ? parseISO(medicationStatement.from) : undefined,
            until: medicationStatement.until ? parseISO(medicationStatement.until) : undefined,
          };
        }),
        conditions:
          details.heurekaDetails?.conditions?.map((condition) => {
            return {
              ...condition,
              from: condition.from ? parseISO(condition.from) : undefined,
              recorded: condition.recorded ? parseISO(condition.recorded) : undefined,
              until: condition.until ? parseISO(condition.until) : undefined,
            };
          }) ?? null,
      }
    : null,
});

function replaceDecimalsWithFractions(input: string): string {
  const replacements: { [key: string]: string } = {
    '0.5': '½',
    '0.25': '¼',
    '0.75': '¾',
  };

  for (const [key, value] of Object.entries(replacements)) {
    input = input.split(key).join(value);
  }

  return input;
}

const transformHeurekaPatient = (patient: HeurekaPatientDto): HeurekaPatient => ({
  ...patient,
  dateOfBirth: patient.dateOfBirth ? parseISO(patient.dateOfBirth) : undefined,
});
